// usePermissions / can() (Phase 32) — the single read path for permission gating. Reads the
// server-driven permission map off the current user; while the map is absent (older payloads,
// pre-backend), falls back to the legacy role derivation so existing gating keeps working.
// Frontend checks are UX only — the backend authorizing every request is the security boundary.
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { permissionVisibleAtTier } from '@/auth/tiers';
import type { PermissionKey, User } from '@/types';

/** Legacy fallback when the user payload carries no permission map. */
function legacyFallback(user: User, permission: PermissionKey): boolean {
  const isOrgAdmin = user.orgRole === 'owner' || user.orgRole === 'admin' || user.isAdmin;
  switch (permission) {
    case 'admin:approve':
      return user.isAdmin;
    case 'org:manage_members':
      return isOrgAdmin;
    case 'org:view':
    case 'billing:view':
      return user.orgRole !== null || user.isAdmin;
    case 'results:export':
      return true; // every member could export in the legacy app
    case 'pe:dataset':
      // Legacy fallback when no server permission map: staff only.
      // (Server compute_permissions grants pe:dataset to all active approved members).
      return user.isAdmin;
    case 'pe:admin':
      // PE admin ops (F28) are staff-only — never the per-user pe:dataset grant.
      return user.isAdmin;
    case 'pe:batch_import':
      // No legacy equivalent, and deliberately not `user.isAdmin`: the grant is an email
      // allowlist the client cannot see, so the only honest fallback is "no". A missing
      // permission map means an old payload, not permission.
      return false;
    case 'pe:batch_edit_holdings':
    case 'pe:enrichment_chain':
      // Same reasoning, and it matters more here: this one gates writes that outrank the scraper
      // permanently. `user.isAdmin` would be exactly wrong — the grant is deliberately narrower
      // than staff, so falling back to it would show the control to every platform admin.
      return false;
  }
}

export function checkPermission(user: User | null, permission: PermissionKey): boolean {
  if (!user) return false;
  const mapped = user.permissions?.[permission];
  if (mapped !== undefined) return mapped;
  return legacyFallback(user, permission);
}

export function usePermissions() {
  const currentUser = useAuthStore((s) => s.currentUser);
  const viewAsTier = useUiStore((s) => s.viewAsTier);

  // View-as preview (admin/role-based-access §5). Two safety properties, both load-bearing:
  //   1. Only a REAL platform admin can preview — the choice is ignored for anyone else, so a
  //      stray sessionStorage value cannot change what a customer sees.
  //   2. The mask is SUBTRACTIVE ONLY. `can()` still starts from the real permission and the
  //      preview can only take it away, never add one. A bug here can hide a control; it cannot
  //      expose one.
  // Note the honest limit: this masks permission checks, not server payloads. Pages that branch on
  // a response field (`data.isAdmin` — which means ORG admin) still render admin content while
  // previewing. That is why the banner says server data is still admin-scoped.
  const isRealPlatformAdmin = checkPermission(currentUser, 'admin:approve');
  const previewTier = viewAsTier !== null && isRealPlatformAdmin ? viewAsTier : null;

  const can = (permission: PermissionKey) => {
    if (!checkPermission(currentUser, permission)) return false;
    if (previewTier === null) return true;
    return permissionVisibleAtTier(permission, previewTier);
  };

  // Unmasked, deliberately. Anything that must stay reachable *during* a preview has to ask the
  // real question — the View-as control itself above all, or entering a preview would hide the
  // control needed to leave it. Use `can('admin:approve')` for ordinary gating; use this only for
  // the preview machinery.
  return { can, previewTier, isRealPlatformAdmin };
}
